import React, { useEffect, useState } from 'react';
import BackupCodes from './BackupCodes';

function errorText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
}

/**
 * Mise en place de la double authentification (T15) : QR code à scanner avec une application d'authentification,
 * premier code pour confirmer, puis codes de secours. Sert à la connexion (admin, obligatoire) et à la page
 * « Sécurité du compte » (membres) : `start()` et `confirm(code)` font les appels, `onDone(session)` reçoit la session
 * renouvelée par le serveur une fois les codes de secours notés.
 */
export default function MfaSetup({ start, confirm, onDone, inputClass = 'field-input', buttonClass = 'btn btn-primary btn-sm' }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    start().then((data) => { if (alive) setSetup(data); })
      .catch((e) => { if (alive) setError(errorText(e, 'La mise en place n’a pas pu commencer. Réessayez.')); });
    return () => { alive = false; };
  }, [start]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      setResult(await confirm(code.trim()));
    } catch (err) {
      setError(errorText(err, 'Code incorrect.'));
    } finally { setBusy(false); }
  };

  if (result) {
    return <BackupCodes codes={result.backup_codes} onContinue={() => onDone(result.session)} buttonClass={buttonClass} />;
  }

  return (
    <div data-testid="mfa-setup">
      <ol style={{ fontSize: 13, paddingLeft: 18, margin: '0 0 12px', display: 'grid', gap: 4 }}>
        <li>Installez une application d'authentification (Google Authenticator, Authy, 1Password, Microsoft Authenticator…).</li>
        <li>Scannez ce QR code avec elle, ou saisissez la clé à la main.</li>
        <li>Saisissez le code à 6 chiffres qu'elle affiche.</li>
      </ol>
      {setup ? (
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          {setup.qr_code && <img src={setup.qr_code} alt="QR code à scanner avec l'application d'authentification" width={160} height={160} data-testid="mfa-qr" />}
          <div style={{ fontSize: 12, minWidth: 0 }}>
            <div style={{ marginBottom: 4 }}>Clé à saisir à la main :</div>
            <code style={{ wordBreak: 'break-all', fontSize: 13, letterSpacing: 1 }} data-testid="mfa-secret">{setup.secret}</code>
          </div>
        </div>
      ) : !error && <p style={{ fontSize: 13 }}>Préparation…</p>}
      <form onSubmit={submit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className={inputClass} inputMode="numeric" autoComplete="one-time-code" maxLength={6} aria-label="Code à 6 chiffres"
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="123456"
          style={{ maxWidth: 140, letterSpacing: 4 }} data-testid="mfa-setup-code" />
        <button type="submit" className={buttonClass} disabled={!setup || code.length !== 6 || busy} data-testid="mfa-setup-confirm">
          Activer
        </button>
      </form>
      {error && <p role="alert" style={{ color: 'var(--red, #DC2626)', fontSize: 13, marginTop: 8 }} data-testid="mfa-error">{error}</p>}
    </div>
  );
}
